import { useContext, useEffect, useRef, useState } from 'react'
import SbUtils from '../../../Utils/SbUtils'
import { __ } from '@wordpress/i18n'
import Button from '../../Common/Button';
import Select from '../../Common/Select';
import DashboardScreenContext from '../../Context/DashboardScreenContext';
import Checkbox from '../../Common/Checkbox';
import BottomUpsellBanner from '../../Global/BottomUpsellBanner';
import Pagination from '../../Common/Pagination';

const FeedsList = () => {

    const sbCustomizer = window.sb_customizer;
    const {
        feeds,
        editorTopLoader,
        editorNotification,
        editorConfirmDialog,
        feedsNumber
    } = useContext( DashboardScreenContext );

    const [ selectedFeeds, setSelectedFeeds ] = useState( [] );
    const [ bulkAction, setBulkAction ] = useState( '' );
    const [ currentPage, setCurrentPage ] = useState(1);
    // Initialize totalPages based on initial feedsCount
    // Use initial feed list length to calculate per page (matches backend RESULTS_PER_PAGE)
    const perPage = feeds.feedsList.length > 0 ? feeds.feedsList.length : 20;
    const initialTotalPages = Math.ceil((feedsNumber.feedsCount || 0) / perPage) || 1;
    const [ totalPages, setTotalPages ] = useState(initialTotalPages);
    const [ isLoading, setIsLoading ] = useState(false);
    const currentPageRef = useRef( currentPage )

    useEffect(() => {
        currentPageRef.current = currentPage
    }, [ currentPage ]);


    const bulkActionsList = {
        ''  : __( 'Bulk Actions', 'sb-customizer' ),
        'delete'  : __( 'Delete', 'sb-customizer' )
    };



    const applyBulkAction = () => {
        if( bulkAction === 'delete' && selectedFeeds.length > 0){
            deleteFeed( null, false ); //This will delete all selected feeds
        }
    }

    const duplicateFeed = ( feedId ) => {
        const formData = {
            action : 'sbr_feed_saver_manager_duplicate_feed',
            feed_id : feedId
        },
        notificationsContent = {
            success : {
                icon : 'success',
                text : __( 'Feed Duplicated', 'sb-customizer' )
            }
        }
        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => { //Call Back Function
                feeds.setFeedsList( data.feedsList );
                setSelectedFeeds( [] );
                feedsNumber.setFeedsCount(data.feedsCount)
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        )
    }


    //Confirm Action to delete feed(s)
    const deleteFeedConfirm = ( feedId, singleFeed = true  ) => {
        let feedsToDelete = [];
        if( singleFeed === true ){
            feedsToDelete.push(feedId);
        }else{
            feedsToDelete = selectedFeeds;
        }

        const formData = {
            action : 'sbr_feed_saver_manager_delete_feeds',
            feeds_ids : JSON.stringify( feedsToDelete )
        },
        notificationsContent = {
            success : {
                icon : 'success',
                text : __( 'Feed Deleted', 'sb-customizer' )
            }
        }
        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => { //Call Back Function
                feeds.setFeedsList( data.feedsList );
                setSelectedFeeds( [] );
                feedsNumber.setFeedsCount(data.feedsCount)
            },
            editorTopLoader,
            editorNotification,
            notificationsContent
        )

    }

    /**
     * Build the delete confirmation content for a feed
     * Returns structured content object for better dialog presentation
     */
    const buildFeedDeleteContent = ( feed, singleFeed ) => {
        if ( !singleFeed ) {
            // Bulk delete - simple message
            return {
                intro: __( 'You are going to delete the selected feeds.', 'sb-customizer' ),
                warning: __( 'You will lose all feed settings.', 'sb-customizer' ),
                outro: __( 'Are you sure you want to continue?', 'sb-customizer' )
            };
        }

        const locationSummary = feed?.location_summary || [];
        const locationCount = locationSummary.length;

        if ( locationCount === 0 ) {
            // Feed is not used anywhere
            return {
                intro: __( 'This feed is not currently used on any pages.', 'sb-customizer' ),
                warning: __( 'You will lose all feed settings.', 'sb-customizer' ),
                outro: __( 'Are you sure you want to continue?', 'sb-customizer' )
            };
        }

        // Build list of locations (max 5)
        const maxDisplay = 5;
        const items = locationSummary.slice( 0, maxDisplay ).map( loc => {
            return loc.page_text || __( 'Unknown page', 'sb-customizer' );
        });

        const moreCount = locationCount > maxDisplay ? locationCount - maxDisplay : 0;
        const placeWord = locationCount === 1 ? __( 'place', 'sb-customizer' ) : __( 'places', 'sb-customizer' );

        return {
            intro: __( 'This feed is used in', 'sb-customizer' ) + ' ' + locationCount + ' ' + placeWord + ':',
            items: items,
            moreCount: moreCount,
            warning: __( 'If you delete this feed, these pages will no longer display reviews.', 'sb-customizer' ) + ' ' + __( 'You will lose all feed settings.', 'sb-customizer' ),
            outro: __( 'Are you sure you want to continue?', 'sb-customizer' )
        };
    }

    //Open Dialog Box to delete feeds
    const deleteFeed = ( feed, singleFeed = true ) => {
        const deleteHeading = singleFeed ? __( 'Delete "', 'sb-customizer' ) + feed.feed_name +'"?'  : __( 'Delete Feeds?', 'sb-customizer' );
        const confirmDialogInfo = {
            active : true,
            heading : deleteHeading,
            content : buildFeedDeleteContent( feed, singleFeed ),
            confirm : {}
        }
        confirmDialogInfo.confirm.onConfirm = () => {
            deleteFeedConfirm( singleFeed ? feed.id : null, singleFeed )
        }
        editorConfirmDialog.setConfirmDialog( confirmDialogInfo )


    }

    const selectFeed = ( feedId ) => {
        const sFeeds = Object.values( selectedFeeds ),
              feedsList = Object.values( feeds.feedsList );
        if( feedId === 'all' ){
            if( sFeeds.length === feedsList.length ){
                setSelectedFeeds( [] );
            }else{
                let feeds = [];
                feedsList.forEach(feed => {
                    feeds.push( feed.id )
                })
                setSelectedFeeds( feeds )
            }
        }else{
            if( !sFeeds.includes( feedId ) ){
                sFeeds.push(feedId)
            }else{
                sFeeds.splice( sFeeds.indexOf( feedId ), 1 );
            }
            setSelectedFeeds(sFeeds)
        }
    }

    const sourcesListNames = ( sourcesList ) => {
        let printedNames = [];
        sourcesList.filter( source => {
            if(!printedNames.includes(source.provider)){
                printedNames.push(source.provider);
            }
            return true;
        } )
        return printedNames.join(', ')
    }

    const fetchFeeds = ( page ) => {
        setIsLoading(true);
        const formData = {
            action : 'sbr_feed_saver_manager_get_feed_list_page',
            page : page
        };

        SbUtils.ajaxPost(
            sbCustomizer.ajaxHandler,
            formData,
            ( data ) => { //Call Back Function
                feeds.setFeedsList( data.feeds || [] );
                feedsNumber.setFeedsCount( data.total || 0 );
                setTotalPages( data.totalPages || 1 );
                setIsLoading(false);
            },
            editorTopLoader,
            null,
            null
        );
    }

    const handlePageChange = ( page ) => {
        setCurrentPage( page );
        fetchFeeds( page );
    }




    return (
        <div className='sb-feedslist-ctn sb-fs'>
            <div className='sb-feedslist-top'>
                <div className='sb-feedslist-bulk-ctn'>
                    <Select
                        size='small'
                        customClass='sb-blulk-select'
                        onChange={ ( event ) => {
                            setBulkAction( event.currentTarget.value );
                        } }
                    >
                        {
                            Object.keys(bulkActionsList).map( action => {
                                return (
                                    <option key={ action } value={ action } >{ bulkActionsList[action] }</option>
                                )
                            })
                        }
                    </Select>
                    <Button
                        type='secondary'
                        size='small'
                        boxshadow='false'
                        text={ __( 'Apply', 'sb-customizer' ) }
                        onClick={ () => {
                            applyBulkAction()
                        } }
                    />
                </div>
                <div className='sb-feedslist-pagination'>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={feedsNumber.feedsCount}
                        onPageChange={handlePageChange}
                        isLoading={isLoading}
                    />
                </div>
            </div>

            <div className='sb-feedslist-table-wrap sb-fs'>
                <table className='sb-feedslist-table  sb-text-tiny'>

                    <thead className='sb-feedslist-thtf sb-feedslist-thead sb-dark2-text'>
                        <tr>
                            <th>
                                <Checkbox
                                    value={ selectedFeeds.length === feeds.feedsList.length }
                                    enabled={ true }
                                    onChange={ () => {
                                        selectFeed( 'all' )
                                    } }
                                />
                            </th>
                            <th>
                                <span>{ __( 'Name', 'sb-customizer' ) }</span>
                            </th>
                            <th>
                                <span>{ __( 'Shortcode', 'sb-customizer' ) }</span>
                            </th>
                            <th>
                                <span>{ __( 'Instances', 'sb-customizer' ) }</span>
                            </th>
                            <th className='cff-fd-lst-act-th'>
                                 <span>{ __( 'Actions', 'sb-customizer' ) }</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody  className='sb-feedslist-tbody'>
                        {
                            feeds.feedsList.map( ( feed, feedIn ) => {
                                let instancesL = feed.location_summary.length;
                                return (
                                    <tr key={feedIn}>
                                        <td>
                                            <Checkbox
                                                value={ selectedFeeds.includes( feed.id ) }
                                                enabled={ true }
                                                onChange={ () => {
                                                    selectFeed( feed.id )
                                                } }
                                            />
                                        </td>
                                        <td className='sb-feedslist-name-ctn'>
                                            <a className='sb-text-small' href={ `${sbCustomizer.builderUrl}&feed_id=${feed.id}` }>{ feed.feed_name }</a>
                                            <span>{sourcesListNames( feed?.sourcesList )}</span>
                                        </td>
                                        <td>
                                            <span>[reviews-feed feed={ feed.id }]</span>
                                            <span className='sb-relative'>
                                                <Button
                                                    type='secondary'
                                                    size='small'
                                                    boxshadow={false}
                                                    icon='copy'
                                                    tooltip={ __( 'Copy Shortcode', 'sb-customizer' ) }
                                                    customClass='sb-feed-copy-btn'
                                                    onClick={ () => {
                                                        SbUtils.copyToClipBoard( `[reviews-feed feed=${ feed.id }]`, editorNotification )
                                                    }}
                                                />
                                            </span>
                                        </td>
                                        <td>
                                            { __( 'Used in ', 'sb-customizer' ) }
                                            <span className={ ( instancesL > 0 && 'sb-bold sb-item-instances-link sb-relative' ) || ''}>
                                                { instancesL } { instancesL === 1 ? __( 'place', 'sb-customizer' ) : __( 'places', 'sb-customizer' ) }
                                                {
                                                    instancesL > 0 &&
                                                    SbUtils.printTooltip( 'Click to view Instances' )
                                                }
                                            </span>
                                        </td>
                                        <td className='sb-feed-item-actions'>
                                            <div className='sb-feed-item-btns'>
                                                <div className='sb-relative'>
                                                    <Button
                                                        type='secondary'
                                                        size='small'
                                                        iconSize='14'
                                                        boxshadow={false}
                                                        icon='pen'
                                                        tooltip={ __( 'Edit', 'sb-customizer' ) }
                                                        onClick={ () => {
                                                            window.location.href = `${sbCustomizer.builderUrl}&feed_id=${feed.id}`
                                                        } }
                                                        customClass='sb-feed-action-btn sb-feed-edit-btn'
                                                    />
                                                </div>
                                                <div className='sb-relative'>
                                                    <Button
                                                        type='secondary'
                                                        size='small'
                                                        iconSize='11'
                                                        boxshadow={false}
                                                        icon='duplicate'
                                                        tooltip={ __( 'Duplicate', 'sb-customizer' ) }
                                                        onClick={ () => {
                                                            duplicateFeed( feed.id )
                                                        } }
                                                        customClass='sb-feed-action-btn sb-feed-duplicate-btn'
                                                    />
                                                </div>
                                                <div className='sb-relative'>
                                                    <Button
                                                        type='secondary'
                                                        size='small'
                                                        iconSize='11'
                                                        boxshadow={false}
                                                        icon='trash'
                                                        tooltip={ __( 'Delete', 'sb-customizer' ) }
                                                        onClick={ () => {
                                                            deleteFeed( feed )
                                                        } }
                                                        customClass='sb-feed-action-btn sb-feed-delete-btn'
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            } )
                        }
                    </tbody>

                    <tfoot className='sb-feedslist-thtf sb-feedslist-tfoot sb-dark2-text'>
                        <tr>
                            <th>
                                <Checkbox
                                    value={ selectedFeeds.length === feeds.feedsList.length }
                                    enabled={ true }
                                    onChange={ () => {
                                        selectFeed( 'all' )
                                    } }
                                />
                            </th>
                            <th>
                                <span className='sb-text-tiny sb-dark2-text'>{ __( 'Name', 'sb-customizer' ) }</span>
                            </th>
                            <th>
                                <span className='sb-text-tiny sb-dark2-text'>{ __( 'Shortcode', 'sb-customizer' ) }</span>
                            </th>
                            <th>
                                <span className='sb-text-tiny sb-dark2-text'>{ __( 'Instances', 'sb-customizer' ) }</span>
                            </th>
                            <th className='cff-fd-lst-act-th'>
                                <span className='sb-text-tiny sb-dark2-text'>{ __( 'Actions', 'sb-customizer' ) }</span>
                            </th>
                        </tr>
                    </tfoot>

                </table>

                {/* Bottom Pagination */}
                <div className='sb-feedslist-bottom'>
                    <div className='sb-feedslist-pagination sb-feedslist-pagination-bottom'>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={feedsNumber.feedsCount}
                            onPageChange={handlePageChange}
                            isLoading={isLoading}
                        />
                    </div>
                </div>

                <BottomUpsellBanner />

            </div>
        </div>
    )
}

export default FeedsList;
